import NotificationCenter from '@/components/shared/NotificationCenter';
import UploadProgressCenter from '@/components/shared/UploadProgressCenter';
import { useHeaderState } from '@/components/contexts/ShellHeaderContext';
import { useSidePanel } from '@/components/contexts/SidePanelContext';
import { cn } from '@/lib/utils';

type ShellHeaderProps = {
  /** Must match the AppShell scroll body's scrollbar-gutter class. */
  scrollbarGutterClass?: string;
};

export function ShellHeader({
  scrollbarGutterClass = '[scrollbar-gutter:stable_both-edges]',
}: ShellHeaderProps) {
  const { header, subheader, isSticky, hideBottomBorder, headerActions, secondaryButtons, headerBelow } = useHeaderState();
  const { isOpen: sidePanelOpen, panelWidth } = useSidePanel();

  const hasActions = Boolean(headerActions || secondaryButtons);
  const hasHeaderContent = Boolean(header || subheader || headerBelow) || hasActions;

  const utilityIcons = (
    <>
      <UploadProgressCenter />
      <NotificationCenter />
    </>
  );

  // Header-less pages (e.g. case-partner) shouldn't get an empty bar pushing
  // content down — just float the utility icons top-right.
  if (!hasHeaderContent) {
    return (
      <div
        className="fixed top-3 z-40 flex items-center gap-0.5 rounded-lg bg-white/90 px-1 py-0.5 text-slate-600 backdrop-blur-xs"
        style={{ right: sidePanelOpen ? `${panelWidth + 20}px` : '20px' }}
      >
        {utilityIcons}
      </div>
    );
  }

  // Border doubles as the divider above headerBelow, so show it for either.
  const showBottomBorder = (Boolean(header) && !hideBottomBorder) || Boolean(headerBelow);

  return (
    <div
      className={cn(
        // The header sits outside the body's scroll region, so it reserves the same
        // scrollbar gutter the body does to keep their content aligned. scrollbar-gutter
        // only applies to scroll containers, hence overflow-hidden — `hidden` (not `auto`)
        // so the header never shows a scrollbar and just grows to fit its content.
        'px-6 bg-primary-50 overflow-hidden',
        scrollbarGutterClass,
        header ? 'pt-4' : 'pt-2',
        showBottomBorder && 'border-b border-slate-200',
        // When headerBelow is present its content (e.g. tabs) sits flush against
        // the bottom border so the active tab underline meets the divider. The
        // 1px bottom padding keeps the active tab's 2px border-b fully inside the
        // padding box: the tablist pulls itself down with -mb-px, so without this
        // the underline straddles the overflow-hidden clip edge and gets rounded
        // away (and vanishes) at non-integer zoom / device-pixel ratios.
        headerBelow ? 'pb-px' : showBottomBorder ? 'pb-4' : 'pb-2',
        isSticky && 'sticky top-0 z-20',
      )}
    >
      <div className="flex items-start justify-between gap-4">
        {header ? (
          <div className="min-w-0 flex-1 pl-10 lg:pl-0">
            <h1 className="min-w-0 [overflow-wrap:anywhere] text-2xl font-semibold text-primary">{header}</h1>
            {subheader && <div className="min-w-0 text-sm text-slate-600 mt-1">{subheader}</div>}
          </div>
        ) : (
          <div className="flex-1" />
        )}
        <div className="flex shrink-0 items-center gap-3 self-center">
          {hasActions && (
            <div className="flex flex-col items-end gap-2">
              {headerActions}
              {secondaryButtons}
            </div>
          )}
          {hasActions && <div className="h-6 w-px bg-slate-200" />}
          <div className="flex items-center gap-0.5 text-slate-600">
            {utilityIcons}
          </div>
        </div>
      </div>
      {headerBelow && <div className="mt-4">{headerBelow}</div>}
    </div>
  );
}
